<template>
  <b-sidebar :id="id ? id : classroom ? 'sidebar-edit-' + classroom.id : 'sidebar-new'" :title="classroom ? classroom.name : $t('sessions.newsession')" sidebar-class="bb-offcanvas-panel"
             v-on:shown="initClassroomGroups"
             bg-variant="white" right backdrop-variant="dark" backdrop shadow>
    <template #default="{ hide }">
      <div v-if="!classroom" class="row form-row" :class="{ 'form-group--error': $v.name.$error }">
        <label class="column col-sm-12 ng-scope form__label">{{ $t("sessions.sessionname") }}</label>
        <b-form-input :id="'name'" type="text" v-model="name"></b-form-input>
      </div>
      <div class="error" v-if="!$v.name.required">{{ $t("sessions.sessionname") }}</div>
      <div class="error" v-if="!$v.name.minLength">{{ $t("sessions.nameshouldhave", { total: $v.name.$params.minLength.min }) }}</div>
      <div v-if="!classroom" class="row form-row" :class="{ 'form-group--error': $v.target_system.$error }">
        <label class="column col-sm-12 ng-scope form__label">{{ $t("sessions.videoconferencesystem") }}</label>
        <b-form-select :id="'system'" :options="target_system_options" v-model="target_system"></b-form-select>
      </div>
      <div class="error" v-if="!$v.target_system.required">{{ $t("sessions.mustselectsystem") }}</div>
      <div class="session-access-container">
        <a class="button secondary" target="_blank" :href="classroom.enrollment.launchUrl" v-if="classroom && classroom.enrollment && classroom.enrollment.launchUrl && !hasEnded(classroom)">
          <location-enter-icon :size="20"/>
          <span>{{ $t("sessions.joinsession") }}</span>
        </a>
        <fieldset>
          <div class="checkbox-wrap list">
            <input :id="classroom ? 'guest-' + classroom.id : 'guest'" type="checkbox" v-model="allowGuest">
            <label :for="classroom ? 'guest-' + classroom.id : 'guest'">{{ $t("sessions.guestaccess") }}</label>
          </div>
        </fieldset>
        <fieldset v-if="classroom && allowGuest && classroom.guestUrl">
          <legend>
            <h3 class="form-label">{{ $t("sessions.guestlink") }}
            </h3>
          </legend>
          <div class="link-with-copy">
            <input :id="classroom ? 'guest-url-' + classroom.id : 'guest-url'" type="text" disabled v-model="guestUrl">
          </div>
        </fieldset>
      </div>
      <div class="row form-row">
        <label class="column col-sm-12 ng-scope" bb-translate="" analytics-id="session.update.date.start-label">{{ $t("sessions.start") }}</label>
        <div class="picker-input date column col-sm-6">
          <b-form-datepicker
            today-button
            v-bind:label-today-button="$t('sessions.today')"
            reset-button
            v-bind:label-reset-button="$t('sessions.clear')"
            v-bind:label-help="$t('sessions.navigatemouse')"
            label-no-date-selected="-"
            :id="classroom ? 'start-date-' + classroom.id : 'start-date'" type="text"
            v-model="startDate"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            locale="es">
          </b-form-datepicker>
        </div>
        <div class="picker-input column col-sm-6">
          <b-form-timepicker
            v-bind:label-close-button="$t('buttons.close')"
            label-no-time-selected="-"
            :id="classroom ? 'start-time-' + classroom.id : 'start-time'" type="text"
            v-model="start_time"
            locale="es">
          </b-form-timepicker>
        </div>
      </div>
      <div class="row form-row" :class="[ 'row form-row', {'row form-row--error': $v.endDate.$error }]">
        <label class="column col-sm-12 ng-scope" bb-translate="" analytics-id="session.update.date.start-label">{{ $t("sessions.end") }}</label>
        <div class="picker-input date column col-sm-6" >
          <b-form-datepicker
            today-button
            v-bind:label-today-button="$t('sessions.today')"
            reset-button
            v-bind:label-reset-button="$t('sessions.clear')"
            v-bind:label-help="$t('sessions.navigatemouse')"
            label-no-date-selected="-"
            :id="classroom ? 'end-date-' + classroom.id : 'end-date'" type="text"
            v-model="endDate"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            locale="es">
          </b-form-datepicker>
        </div>
        <div class="picker-input column col-sm-6">
          <b-form-timepicker
            v-bind:label-close-button="$t('buttons.close')"
            label-no-time-selected="-"
            :id="classroom ? 'end-time-' + classroom.id : 'end-time'" type="text"
            v-model="end_time"
            locale="es">
          </b-form-timepicker>
        </div>
      </div>
      <div class="error" v-if="!$v.endDate.minValue">{{ $t("sessions.dateendcantbeforestart") }}</div>
      <div v-if="options_groups && options_groups.length > 0" class="row form-row" :class="{ 'form-group--error': $v.selected_groups.$error }">
        <label class="column col-sm-12 ng-scope form__label">{{ $t("sessions.groups") }}</label>
        <multiselect v-model="selected_groups" :options="options_groups" :searchable="false" :multiple="true" :close-on-select="true" v-bind:placeholder="$t('sessions.targetgroups')"
                     :allow-empty="false" :hide-selected="true" :show-labels="false" track-by="name" label="name">
        </multiselect>
      </div>
      <div class="error" v-if="!$v.selected_groups.required">{{ $t("sessions.mustselectgroup") }}</div>
      <fieldset>
        <legend>
          <h3 class="form-label">{{ $t("sessions.allowrecordingdownload") }}
          </h3>
        </legend>
        <div class="checkbox-wrap list">
          <input :id="classroom ? 'download-recording-' + classroom.id : 'download-recording'" type="checkbox" v-model="allowRecordingDownload">
          <label :for="classroom ? 'download-recording-' + classroom.id : 'download-recording'">{{ $t("sessions.allowrecordingdownload") }}</label>
        </div>
      </fieldset>
      <fieldset>
        <legend>
          <h3 class="form-label">{{ $t("sessions.allowphone") }}</h3>
        </legend>
        <div class="checkbox-wrap list">
          <input :id="classroom ? 'enable-session-telephony-' + classroom.id : 'enable-session-telephony'" type="checkbox" v-model="allowSessionPhone">
          <label :for="classroom ? 'enable-session-telephony-' + classroom.id : 'enable-session-telephony'">{{ $t("sessions.allowjoinphone") }}</label>
        </div>
      </fieldset>
      <fieldset v-if="target_system=='ZOOM' || (classroom && classroom.targetSystem=='ZOOM')">
        <legend>
          <h3 class="form-label">{{ $t("sessions.survey") }}</h3>
        </legend>
        <div class="checkbox-wrap list">
          <input :id="classroom ? 'enable-session-survey-' + classroom.id : 'enable-session-survey'" type="checkbox" v-model="enableSurvey">
          <label :for="classroom ? 'enable-session-survey-' + classroom.id : 'enable-session-survey'">{{ $t("sessions.enablesurvey") }}</label>
        </div>
      </fieldset>
      <fieldset v-if="target_system=='ZOOM' || (classroom && classroom.targetSystem=='ZOOM')">
        <legend>
          <h3 class="form-label">{{ $t("sessions.bigscalesessionzoom") }}</h3>
        </legend>
        <div class="checkbox-wrap list">
          <input :id="classroom ? 'large-session-enable-' + classroom.id : 'large-session-enable'" type="checkbox" v-model="largeSessionEnable">
          <label :for="classroom ? 'large-session-enable-' + classroom.id : 'large-session-enable'" style="padding-top: .5%;">{{ $t("sessions.allowmoreparticipantszoom") }}</label>
        </div>
      </fieldset>
      <fieldset v-if="target_system=='CONNECT' || (classroom && classroom.targetSystem=='CONNECT')">
        <legend>
          <h3 class="form-label">{{ $t("sessions.webrtc") }}</h3>
        </legend>
        <div class="checkbox-wrap list">
          <input :id="classroom ? 'webrtc-enable-' + classroom.id : 'webrtc-enable'" type="checkbox" v-model="webRtcEnable">
          <label :for="classroom ? 'webrtc-enable-' + classroom.id : 'webrtc-enable'">{{ $t("sessions.webrtc") }}</label>
        </div>
      </fieldset>
      <div v-if="target_system=='COLLAB' || (classroom && classroom.targetSystem=='COLLAB')" class="row form-row">
        <label class="column col-sm-12 ng-scope form__label">{{ $t("sessions.earlyadmision") }}</label>
        <b-form-select :id="classroom ? 'boundary-time-' + classroom.id : 'boundary-time'" :options="boundary_time_options" v-model="boundaryTime"></b-form-select>
      </div>
      <fieldset v-if="target_system=='COLLAB' || (classroom && classroom.targetSystem=='COLLAB')">
        <legend>
          <h3 class="form-label">{{ $t("sessions.bigscalesession") }}</h3>
        </legend>
        <div class="checkbox-wrap list">
          <input :id="classroom ? 'large-session-enable-' + classroom.id : 'large-session-enable'" type="checkbox" v-model="largeSessionEnable">
          <label :for="classroom ? 'large-session-enable-' + classroom.id : 'large-session-enable'">{{ $t("sessions.allowmoreparticipants") }}</label>
        </div>
      </fieldset>
    </template>
    <template #footer="{ hide }">
      <div class="save-buttons panel-footer">
        <div class="buttons-2 equal-buttons row">
          <div class="col-sm-6">
            <button class="button secondary cancel-button" @click="hide">{{ $t("buttons.cancel") }}</button>
          </div>
          <div class="col-sm-6">
            <button class="button save-button" @click="updateSession">{{ $t("buttons.save") }}</button>
          </div>
        </div>
      </div>
    </template>
  </b-sidebar>
</template>

<script>
import CloseIcon from 'vue-material-design-icons/Close.vue'
import LocationEnterIcon from 'vue-material-design-icons/LocationEnter.vue'
import utilitiesMixin from '../../../mixins/utilitiesMixin'
import jwtMixin from '../../../mixins/jwtMixin'
import Multiselect from 'vue-multiselect'
import { minLength, requiredIf } from 'vuelidate/lib/validators'

export default {
  name: 'EditClassroomAdminSidebar',
  components: {CloseIcon, LocationEnterIcon, Multiselect},
  mixins: [utilitiesMixin, jwtMixin],
  props: ['classroom', 'groups', 'id'],
  mounted () {
    this.$moment.locale('es')
  },
  created () {
    this.$i18n.locale = this.jwtLanguage()
  },
  data () {
    return {
      site_groups: [],
      startDate: null,
      start_time: null,
      endDate: null,
      end_time: null,
      allowGuest: false,
      allowRecordingDownload: false,
      allowSessionPhone: false,
      largeSessionEnable: false,
      enableSurvey: false,
      webRtcEnable: false,
      acInstanceId: null,
      boundaryTime: null,
      guestUrl: null,
      name: null,
      options_groups: [],
      selected_groups: [],
      unalterable_groups: [],
      target_system: null,
      target_system_options: this.targetSystemOptions(),
      boundary_time_options: this.boundaryTimeOptions()
    }
  },
  validations: {
    name: {
      required: requiredIf(function () {
        return !this.classroom
      }),
      minLength: minLength(4)
    },
    target_system: {
      required: requiredIf(function () {
        return !this.classroom
      })
    },
    selected_groups: {
      required: requiredIf(function () {
        return this.options_groups && this.options_groups.length > 0
      })
    },
    endDate: {
      minValue (endDate, { startDate }) {
        return window.moment(endDate + ' ' + this.end_time) > window.moment(startDate + ' ' + this.start_time)
      }
    }
  },
  methods: {
    setName (value) {
      this.name = value
      this.$v.name.$touch()
    },
    setTargetSystem (value) {
      this.target_system = value
      this.$v.target_system.$touch()
    },
    setSelectedGroups (value) {
      this.selected_groups = value
      this.$v.selected_groups.$touch()
    },
    updateSession () {
      let vueComponent = this

      // CMM: Validación previa al envío de formulario
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }

      // CMM: Se distingue si se está actualizando una sesión o es una nueva
      if (this.classroom) {
        // CMM: Se establece la fecha y hora
        this.$set(this.classroom, 'start', window.moment(this.startDate + ' ' + this.start_time).utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]'))
        this.$set(this.classroom, 'end', window.moment(this.endDate + ' ' + this.end_time).utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]'))
        this.$set(this.classroom, 'allowGuest', this.allowGuest)
        this.$set(this.classroom, 'allowRecordingDownload', this.allowRecordingDownload)
        this.$set(this.classroom, 'allowSessionPhone', this.allowSessionPhone)
        this.$set(this.classroom, 'boundaryTime', this.boundaryTime)
        this.$set(this.classroom, 'largeSession', this.largeSessionEnable)
        this.$set(this.classroom, 'enableSurvey', this.enableSurvey)
        this.$set(this.classroom, 'webRtc', this.webRtcEnable)
        this.$set(this.classroom, 'acInstanceId', this.acInstanceId)
        this.$set(this.classroom, 'unirGroups', this.selected_groups.map(group => group.unirid).concat(this.unalterable_groups))
        window.axios.put('classrooms/' + this.classroom.id, this.classroom).then((response) => {
          if (response.status === 200) {
            vueComponent.$noty.success(vueComponent.$i18n.t('sessions.successsaveconf'), {
              timeout: 3000,
              progressBar: true
            })
            // Si se viene de una reutilizacion de una sesion
            if (vueComponent.id) {
              // Se reinicia el modelo y la validación, se oculta el modal y se recarga la lista
              Object.assign(this.$data, this.$options.data.call(this))
              vueComponent.$v.$reset()
              this.$root.$emit('bv::toggle::collapse', vueComponent.id)
              vueComponent.$emit('refresh')
            }
          }
        })
      } else {
        // CMM: Se crea una nueva sesión
        let newClassroom = {
          name: this.name,
          targetSystem: this.target_system,
          unirGroups: this.selected_groups.map(group => group.unirid).concat(this.unalterable_groups),
          start: window.moment(this.startDate + ' ' + this.start_time).utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
          end: window.moment(this.endDate + ' ' + this.end_time).utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
          active: true,
          allowRecordingDownload: this.allowRecordingDownload,
          allowSessionPhone: this.allowSessionPhone,
          allowGuest: this.allowGuest,
          boundaryTime: this.boundaryTime,
          largeSession: this.largeSessionEnable,
          enableSurvey: this.enableSurvey,
          webRtc: this.webRtcEnable,
          acInstanceId: this.target_system === 'CONNECT' ? this.jwtDefaultAcInstance() : null,
          fromGcc: false
        }
        window.axios.post('classrooms/', newClassroom).then((response) => {
          if (response.status === 200) {
            vueComponent.$noty.success(vueComponent.$i18n.t('sessions.successsave'), {
              timeout: 3000,
              progressBar: true
            })
            // CMM: Se reinicia el modelo y la validación, se oculta el modal y se recarga la lista
            Object.assign(this.$data, this.$options.data.call(this))
            vueComponent.$v.$reset()
            this.$root.$emit('bv::toggle::collapse', 'sidebar-new')
            vueComponent.$emit('refresh')
          }
        })
      }
    },
    initClassroomGroups () {
      let vueComponent = this

      let now = window.moment()
      this.startDate = this.classroom ? window.moment(this.classroom.start).format('YYYY-MM-DD') : now.format('YYYY-MM-DD')
      this.start_time = this.classroom ? window.moment(this.classroom.start).format('HH:mm') : now.format('HH:mm')
      now = now.add(1, 'hours')
      this.endDate = this.classroom ? window.moment(this.classroom.end).format('YYYY-MM-DD') : now.format('YYYY-MM-DD')
      this.end_time = this.classroom ? window.moment(this.classroom.end).format('HH:mm') : now.format('HH:mm')
      this.allowGuest = this.classroom ? this.classroom.allowGuest : false
      this.allowRecordingDownload = this.classroom ? this.classroom.allowRecordingDownload : false
      this.allowSessionPhone = this.classroom ? this.classroom.allowSessionPhone : false
      this.guestUrl = this.classroom ? this.classroom.guestUrl : null
      this.boundaryTime = this.classroom ? this.classroom.boundaryTime : this.boundaryTimeDefaultOption()
      this.largeSessionEnable = this.classroom ? this.classroom.largeSession : false
      this.enableSurvey = this.classroom ? this.classroom.enableSurvey : false
      this.webRtcEnable = this.classroom ? this.classroom.webRtc : false
      this.acInstanceId = this.classroom ? this.classroom.acInstanceId : null
      this.options_groups = this.groups

      // CMM: Se recuperan los grupos asociados a la clase dado que del listado general en el LTI unicamente viene asociado uno por el tipo de filtrado que se realiza
      if (this.classroom) {
        window.axios.get('classrooms/', {params: {id: this.classroom.id}}).then((response) => {
          // CMM: Se efectúan las matrículas en los grupos y se recuperan las URLs de acceso
          let classrooms = response.data.results
          if (vueComponent.groups && vueComponent.groups.length > 0 && classrooms[0]) {
            vueComponent.selected_groups = vueComponent.groups.filter(
              group => classrooms[0].unirGroups.findIndex(unirGroup => unirGroup === group.unirid) !== -1
            )
            // CMM: Pueden exitir grupos que sean de otras aulas/campus y que no serán alterables desde el aula actual
            vueComponent.unalterable_groups = classrooms[0].unirGroups.filter(
              unirGroup => vueComponent.groups.findIndex(group => unirGroup === group.unirid) === -1
            )
          } else if (classrooms[0]) {
            vueComponent.unalterable_groups = classrooms[0].unirGroups
          }
        })
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Admin/Modals/EditClassroomAdminSidebar.vue